import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ContextBar } from "../components/context-bar";
import { AdvancedJson, CountChip, Disclosure, KeyVal, SeverityBadge, StateTag, formatAge, shortHash } from "../components/phase3";
import { ConfirmGate, SafeError, approvalTone, deploymentTone } from "../components/phase4";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type ApprovalView,
  type DeploymentView,
  type DiffOperation,
  type DryRunPlan,
  type PersonaComparison,
  type PlatformStatus,
  type SafetyFinding,
} from "../lib/api";

/**
 * The exact confirmation an approver must acknowledge. Kept as an exported
 * constant so it is impossible to silently weaken and so tests assert on it
 * verbatim. Approval is an explicit action — never inferred from viewing.
 */
export const APPROVAL_CONFIRMATION_TEXT =
  "This approval applies ONLY to this exact plan. A changed server or a changed blueprint requires a new approval. " +
  "By approving you are authorizing REAL changes to your live Discord server. Rollback is planned as a separate " +
  "controlled step and is NOT guaranteed to fully restore the prior state.";

// ── Presentational review (testable with mock data, no data fetching) ─────────
export function ReleaseReviewView({
  deployment,
  plan,
  approval,
  platform,
  now = Date.now(),
  busy = false,
  actionError = null,
  onRequestApproval,
  onApprove,
  onReject,
  onRevoke,
  onQueue,
  onCancel,
}: {
  deployment: DeploymentView;
  plan: DryRunPlan | null;
  approval: ApprovalView | null;
  platform: PlatformStatus | null;
  now?: number;
  busy?: boolean;
  actionError?: string | null;
  onRequestApproval?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  onRevoke?: () => void;
  onQueue?: () => void;
  onCancel?: () => void;
}): JSX.Element {
  const status = deployment.status;
  const blocked = status === "BLOCKED";
  const findings = plan?.findings ?? [];
  const operations = plan?.operations ?? [];
  const comparison = plan?.personaSimulation?.comparison ?? [];

  const destructiveCount = useMemo(() => operations.filter((o) => o.destructive).length, [operations]);
  const protectedCount = useMemo(() => operations.filter((o) => o.protectedResource).length, [operations]);

  const approvalExpired = approval ? Date.parse(approval.expiresAt) <= now : false;

  return (
    <div className="release-review">
      {/* ── Immutable identity ─────────────────────────────────────────── */}
      <Card title="Release identity">
        <div className="kv-grid">
          <KeyVal
            k="Status"
            v={<StatusIndicator tone={deploymentTone(status)} label={status} />}
          />
          <KeyVal k="Blueprint version" v={deployment.blueprintVersionId} mono />
          <KeyVal k="Blueprint version hash" v={shortHash(deployment.blueprintVersionHash)} mono />
          <KeyVal k="Plan hash" v={shortHash(deployment.planHash)} mono />
          <KeyVal k="Current snapshot hash" v={shortHash(deployment.currentSnapshotHash)} mono />
          <KeyVal k="Safety policy" v={deployment.safetyPolicyVersion} mono />
          <KeyVal k="Prepared" v={formatAge(deployment.createdAt, now)} />
          <KeyVal k="Prepared by" v={deployment.requestedByUserId} mono />
        </div>
        <p className="section-note">
          The plan, snapshot and blueprint hashes above are bound to this deployment. If any of them changes, this
          release becomes <strong>STALE</strong> and must be regenerated and re-approved — an existing approval can never
          carry over.
        </p>
      </Card>

      {/* ── Environment + locks ────────────────────────────────────────── */}
      <Card title="Deployment environment & locks">
        <div className="kv-grid">
          <KeyVal k="Environment" v={platform?.environment ?? "unknown"} />
          <KeyVal
            k="Deployment lock"
            v={
              <StatusIndicator
                tone={platform?.liveDeploymentLocked ? "ok" : "warn"}
                label={platform?.liveDeploymentLocked ? "Locked (safe)" : "Unlocked"}
              />
            }
          />
          <KeyVal
            k="Production lock (live mutations)"
            v={
              <StatusIndicator
                tone={platform?.safety.liveMutationsAllowed ? "warn" : "ok"}
                label={platform?.safety.liveMutationsAllowed ? "Live mutations allowed" : "Live mutations blocked"}
              />
            }
          />
          <KeyVal k="Requires approval" v={platform?.safety.requireApproval ? "yes" : "no"} />
        </div>
        {platform && platform.liveDeploymentLocked ? (
          <p className="section-note">
            Deployment is currently locked at the platform level. Even an approved release cannot reach Discord while the
            lock is engaged; the worker performs the authoritative live recheck and remains locked by default.
          </p>
        ) : null}
      </Card>

      {/* ── Approval status ────────────────────────────────────────────── */}
      <Card title="Approval status">
        {approval ? (
          <div className="kv-grid">
            <KeyVal k="Approval" v={<StatusIndicator tone={approvalTone(approval.status)} label={approval.status} />} />
            <KeyVal
              k="Expires"
              v={approvalExpired ? <span className="dryrun-blocked">expired ({formatAge(approval.expiresAt, now)})</span> : formatAge(approval.expiresAt, now)}
            />
            <KeyVal k="Approver" v={approval.approverUserId ?? "—"} mono />
            <KeyVal k="Approver role" v={approval.approverRole ?? "—"} />
            <KeyVal k="Approval bound to plan" v={shortHash(approval.planHash)} mono />
            {approval.approvalNote ? <KeyVal k="Note" v={approval.approvalNote} /> : null}
          </div>
        ) : (
          <p className="muted">No approval has been requested for this deployment yet.</p>
        )}
      </Card>

      {/* ── Proposed operations (derived server-side; read-only here) ──── */}
      <section>
        <h2 className="section-title">
          Proposed operations <StateTag state="proposed" />
        </h2>
        <p className="section-note">
          These operations are derived by the server from the immutable plan. The dashboard never submits an operation
          list.
        </p>
        <div className="row" style={{ marginBottom: "0.75rem" }}>
          <CountChip label="operations" value={operations.length} tone="neutral" />
          <CountChip label="destructive" value={destructiveCount} tone={destructiveCount ? "danger" : "neutral"} />
          <CountChip label="protected" value={protectedCount} tone={protectedCount ? "warn" : "neutral"} />
        </div>
        <Card>
          {operations.length === 0 ? (
            <p className="muted">{plan ? "No operations in this plan." : "Plan detail is not available."}</p>
          ) : (
            <div className="op-list">
              {operations.map((op, i) => (
                <OperationRow key={op.operationId ?? `${op.resourceKey}-${i}`} op={op} />
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* ── Safety findings ────────────────────────────────────────────── */}
      <section>
        <h2 className="section-title">Safety findings</h2>
        <Card>
          {findings.length === 0 ? (
            <p className="muted">No safety findings were raised.</p>
          ) : (
            <div className="finding-list">
              {findings.map((f, i) => (
                <FindingRow key={`${f.code}-${i}`} finding={f} />
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* ── Persona simulation ─────────────────────────────────────────── */}
      <section>
        <h2 className="section-title">Persona simulation</h2>
        <Card>
          {comparison.length === 0 ? (
            <p className="muted">No persona comparison was produced.</p>
          ) : (
            <div className="persona-list">
              {comparison.map((c) => (
                <PersonaDiffRow key={c.personaId} cmp={c} />
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* ── Decision area ──────────────────────────────────────────────── */}
      <section>
        <h2 className="section-title">Decision</h2>
        {actionError ? <SafeError message={actionError} /> : null}

        {blocked ? (
          <Card>
            <p className="dryrun-blocked">
              This release is <strong>BLOCKED</strong>. It cannot be approved or queued until the blocking findings are
              resolved and a new plan is generated.
            </p>
          </Card>
        ) : null}

        {status === "PLANNED" ? (
          <Card title="Request approval" description="A planned release must have approval requested before it can be approved.">
            <Button variant="secondary" disabled={busy} onClick={() => onRequestApproval?.()}>
              {busy ? "Working…" : "Request approval"}
            </Button>
          </Card>
        ) : null}

        {status === "AWAITING_APPROVAL" ? (
          <Card title="Approve this exact plan">
            <div className="dryrun-blocked" role="note" style={{ marginBottom: "0.75rem" }}>
              <strong>Read before approving.</strong>
              <p style={{ margin: "0.5rem 0 0" }}>{APPROVAL_CONFIRMATION_TEXT}</p>
              <ul style={{ margin: "0.5rem 0 0" }}>
                <li>This approval applies ONLY to this exact plan ({shortHash(deployment.planHash)}).</li>
                <li>A changed server or blueprint requires a brand-new approval.</li>
                <li>You are authorizing REAL changes to your live Discord server.</li>
                <li>Rollback is a separate controlled step and is not guaranteed.</li>
              </ul>
            </div>
            <ConfirmGate
              terms={
                <p className="muted">
                  Tick the box to confirm you have read the terms above. The approve action stays disabled until you do —
                  there is no one-click approval.
                </p>
              }
              acknowledgeLabel="I have read the terms and I authorize real Discord changes for this exact plan."
              confirmLabel="Approve this exact plan"
              busy={busy}
              onConfirm={() => onApprove?.()}
            />
            <div className="row" style={{ marginTop: "0.75rem" }}>
              <Button variant="ghost" disabled={busy} onClick={() => onReject?.()}>
                Reject
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => onCancel?.()}>
                Cancel deployment
              </Button>
            </div>
          </Card>
        ) : null}

        {status === "APPROVED" ? (
          <Card title="Queue for deployment" description="Queueing revalidates the bound approval and hands the release to the worker. The worker performs the authoritative live recheck.">
            <div className="row">
              <Button variant="primary" disabled={busy} onClick={() => onQueue?.()}>
                {busy ? "Working…" : "Queue deployment"}
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => onRevoke?.()}>
                Revoke approval
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => onCancel?.()}>
                Cancel deployment
              </Button>
            </div>
          </Card>
        ) : null}
      </section>

      <AdvancedJson title="Advanced — read-only deployment record (schema context)" value={deployment} />
    </div>
  );
}

function OperationRow({ op }: { op: DiffOperation }): JSX.Element {
  return (
    <div className="op-row">
      <div className="op-row__head">
        <span className="op-row__action">{op.operationType}</span>
        <code className="op-row__key">{op.resourceKey}</code>
        <SeverityBadge severity={op.severity} />
        {op.protectedResource ? <span className="marker marker--protected" title="Protected resource">protected</span> : null}
        {op.destructive ? <span className="marker marker--destructive" title="Destructive change">destructive</span> : null}
      </div>
      <p className="op-row__reason">{op.reason}</p>
      {(op.dependencies ?? []).length > 0 ? <p className="op-row__deps">Depends on: {op.dependencies.join(", ")}</p> : null}
      <Disclosure summary="Technical detail">
        <pre className="json-view">{JSON.stringify(op, null, 2)}</pre>
      </Disclosure>
    </div>
  );
}

function FindingRow({ finding }: { finding: SafetyFinding }): JSX.Element {
  return (
    <div className="finding-row">
      <div className="op-row__head">
        <SeverityBadge severity={finding.severity} />
        <strong>{finding.title}</strong>
        {finding.blocksDeployment ? <span className="marker marker--destructive">blocks deployment</span> : null}
      </div>
      <p className="finding-row__plain">{finding.plain}</p>
      {finding.remediation ? (
        <p className="finding-row__fix">
          <span className="finding-row__fix-label">How to fix:</span> {finding.remediation}
        </p>
      ) : null}
    </div>
  );
}

function PersonaDiffRow({ cmp }: { cmp: PersonaComparison }): JSX.Element {
  return (
    <div className="persona-row">
      <div className="op-row__head">
        <strong>{cmp.label}</strong>
        <span className="muted">
          visible: {cmp.current.visibleChannels?.length ?? 0} <span aria-hidden="true">→</span>{" "}
          {cmp.desired.visibleChannels?.length ?? 0}
        </span>
        {cmp.desired.isAdministrator ? <span className="marker marker--destructive">administrator</span> : null}
      </div>
      <div className="persona-row__diffs">
        <div>
          <span className="diff-label diff-label--add">Newly visible</span>
          {(cmp.newlyVisible ?? []).length === 0 ? <span className="muted"> none</span> : <ul>{cmp.newlyVisible.map((id) => <li key={id}>{id}</li>)}</ul>}
        </div>
        <div>
          <span className="diff-label diff-label--remove">Newly hidden</span>
          {(cmp.newlyHidden ?? []).length === 0 ? <span className="muted"> none</span> : <ul>{cmp.newlyHidden.map((id) => <li key={id}>{id}</li>)}</ul>}
        </div>
      </div>
    </div>
  );
}

// ── Data-fetching container ──────────────────────────────────────────────────
export function ReleaseReview(): JSX.Element {
  const ctx = useAppContext();
  const [list, setList] = useState<DeploymentView[] | null>(null);
  const [deployment, setDeployment] = useState<DeploymentView | null>(null);
  const [plan, setPlan] = useState<DryRunPlan | null>(null);
  const [approval, setApproval] = useState<ApprovalView | null>(null);
  const [platform, setPlatform] = useState<PlatformStatus | null>(null);
  const [dryRunPlanId, setDryRunPlanId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const ready = ctx.tenantId && ctx.guildId;

  useEffect(() => {
    api.status().then(setPlatform).catch(() => setPlatform(null));
  }, []);

  const loadList = () => {
    if (!ctx.tenantId || !ctx.guildId) return;
    setError(null);
    api
      .listDeployments(ctx.tenantId, ctx.guildId)
      .then((r) => setList(r.deployments))
      .catch((e) => setError(e instanceof ApiError ? e.message : "Could not load deployments"));
  };

  useEffect(() => {
    if (!ready) {
      setList(null);
      setDeployment(null);
      return;
    }
    loadList();
  }, [ctx.tenantId, ctx.guildId]); // loaders read the same context values

  const open = async (id: string) => {
    if (!ctx.tenantId || !ctx.guildId) return;
    setActionError(null);
    setApproval(null);
    setPlan(null);
    try {
      const dep = await api.getDeployment(ctx.tenantId, id);
      setDeployment(dep);
      if (dep.dryRunPlanId) {
        const run = await api.getDryRun(ctx.tenantId, ctx.guildId, dep.dryRunPlanId);
        setPlan(run.content);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load the deployment");
    }
  };

  const create = async () => {
    if (!ctx.tenantId || !ctx.guildId) return;
    setBusy(true);
    setActionError(null);
    try {
      const dep = await api.createDeployment(ctx.tenantId, ctx.guildId, dryRunPlanId.trim());
      setDeployment(dep);
      await open(dep.id);
      loadList();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : "Could not prepare a deployment");
    } finally {
      setBusy(false);
    }
  };

  // Wrap an approval-lifecycle action; refreshes deployment + approval from the server.
  const runAction = async (fn: () => Promise<void>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      loadList();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : "The action could not be completed");
    } finally {
      setBusy(false);
    }
  };

  const tid = ctx.tenantId ?? "";
  const requestApproval = () =>
    runAction(async () => {
      if (!deployment) return;
      const r = await api.requestApproval(tid, deployment.id);
      setDeployment(r.deployment);
      setApproval(r.approval);
    });
  const approve = () =>
    runAction(async () => {
      if (!deployment) return;
      const r = await api.approveDeployment(tid, deployment.id);
      setDeployment(r.deployment);
      setApproval(r.approval);
    });
  const reject = () =>
    runAction(async () => {
      if (!deployment) return;
      const a = await api.rejectDeployment(tid, deployment.id);
      setApproval(a);
      await open(deployment.id);
    });
  const revoke = () =>
    runAction(async () => {
      if (!deployment) return;
      const a = await api.revokeApproval(tid, deployment.id);
      setApproval(a);
      await open(deployment.id);
    });
  const queue = () =>
    runAction(async () => {
      if (!deployment) return;
      const d = await api.queueDeployment(tid, deployment.id);
      setDeployment(d);
    });
  const cancel = () =>
    runAction(async () => {
      if (!deployment) return;
      const d = await api.cancelDeployment(tid, deployment.id);
      setDeployment(d);
    });

  return (
    <>
      <PageHeader title="Release review">
        <p>
          Review, approve and queue a controlled deployment. Approval authorizes REAL Discord changes and is bound to
          one exact plan. Track a queued release on the <Link to="/app/deployment">deployment progress</Link> page.
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to review releases.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar requireGuild />
          {!ready ? (
            <EmptyState title="Select a workspace and server">
              Choose a workspace and enter your ODIN server id above to review releases.
            </EmptyState>
          ) : (
            <>
              <Card title="Prepare a deployment" description="Bind a READY dry-run plan to a new controlled deployment. Operations are derived server-side from the immutable plan.">
                <div className="row">
                  <input
                    className="field__input"
                    placeholder="Dry-run plan id"
                    value={dryRunPlanId}
                    onChange={(e) => setDryRunPlanId(e.target.value)}
                    aria-label="Dry-run plan id"
                  />
                  <Button variant="secondary" disabled={busy || dryRunPlanId.trim().length < 1} onClick={create}>
                    {busy ? "Preparing…" : "Prepare deployment"}
                  </Button>
                </div>
              </Card>

              {error ? <ApiUnavailableState detail={error} /> : null}
              {list === null ? <LoadingState label="Loading deployments…" /> : null}

              {list && list.length > 0 ? (
                <Card title="Deployments">
                  <div className="op-list">
                    {list.map((d) => (
                      <button key={d.id} className="list-row" onClick={() => open(d.id)}>
                        <StatusIndicator tone={deploymentTone(d.status)} label={d.status} />
                        <code>{shortHash(d.planHash)}</code>
                        <span className="muted">{formatAge(d.createdAt)}</span>
                      </button>
                    ))}
                  </div>
                </Card>
              ) : list && list.length === 0 ? (
                <EmptyState title="No deployments yet">Prepare one above from a dry-run plan.</EmptyState>
              ) : null}

              {deployment ? (
                <ReleaseReviewView
                  deployment={deployment}
                  plan={plan}
                  approval={approval}
                  platform={platform}
                  busy={busy}
                  actionError={actionError}
                  onRequestApproval={requestApproval}
                  onApprove={approve}
                  onReject={reject}
                  onRevoke={revoke}
                  onQueue={queue}
                  onCancel={cancel}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
}
